import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaCalendarCheck,
  FaCheck,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaExclamationTriangle,
} from "react-icons/fa";

import http from "../api/apiClient";
import ErrorPage from "../components/ErrorPage";
import Spinner from "../components/Spinner";
import {
  LdsOverviewHero,
  LdsOverviewPage,
  LdsOverviewSection,
} from "../components/lds/LdsOverview";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";
import { parseToDate } from "../utils/dateUtils";
import { translateClassName } from "../utils/classroomUtils";
import { useCan } from "../authz/useCan";
import {
  compareClassroomNames,
  compareClassroomTypes,
} from "./SchoolStructure/classrooms/classroomSorting";
import { teacherName } from "./SchoolStructure/utils";
import type { TeacherAssignment } from "./SchoolStructure/types";

type Identifier = string | number;

interface SchoolYear {
  id: Identifier;
  startDate?: unknown;
  endDate?: unknown;
}

interface Classroom {
  id: Identifier;
  className: string;
  classType?: string | null;
  teachers?: TeacherAssignment[] | null;
}

interface ClassroomChild {
  id: Identifier;
  firstName?: string;
  lastName?: string;
  current?: boolean | null;
}

interface ClassroomAssignment {
  current?: boolean;
  startDate?: unknown;
  endDate?: unknown;
}

interface ChildMismatch extends ClassroomChild {
  currentSchoolYearLabel: string;
}

interface ClassroomCheck {
  classroom: Classroom;
  total: number;
  correct: number;
  mismatches: ChildMismatch[];
}

const toArray = <T,>(value: unknown): T[] =>
  Array.isArray(value) ? (value as T[]) : [];

const schoolYearLabel = (startValue: unknown, endValue: unknown): string => {
  const start = parseToDate(startValue);
  const end = parseToDate(endValue);
  if (!start || !end) return "—";
  return `${start.getFullYear()} - ${end.getFullYear()}`;
};

const findCurrentSchoolYear = (years: SchoolYear[]): SchoolYear | undefined => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return years.find((year) => {
    const start = parseToDate(year.startDate);
    const end = parseToDate(year.endDate);
    return Boolean(start && end && start <= today && end >= today);
  });
};

const childName = (child: ClassroomChild): string =>
  [child.firstName, child.lastName].filter(Boolean).join(" ") || "—";

export default function CurrentSchoolYearSignals(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canManageCurrentSchoolYearSignal = useCan("signals.current-school-year", "manage");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message?: string; status?: number } | null>(null);
  const [year, setYear] = useState<SchoolYear | null>(null);
  const [checks, setChecks] = useState<ClassroomCheck[]>([]);
  const [expandedClassrooms, setExpandedClassrooms] = useState<Set<string>>(
    () => new Set()
  );
  const [updatingChildren, setUpdatingChildren] = useState<Set<string>>(
    () => new Set()
  );
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;

    const load = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const yearsResponse = await http.get("/years");
        const currentYear = findCurrentSchoolYear(
          toArray<SchoolYear>(yearsResponse.data)
        );
        if (!currentYear) {
          if (!cancelled) {
            setYear(null);
            setChecks([]);
          }
          return;
        }

        const classroomsResponse = await http.get("/classrooms", {
          params: { year: currentYear.id },
        });
        const classrooms = toArray<Classroom>(classroomsResponse.data).sort(
          (left, right) =>
            compareClassroomTypes(left, right) ||
            compareClassroomNames(left.className, right.className)
        );

        const childrenByClassroom = await Promise.all(
          classrooms.map(async (classroom) => {
            const response = await http.get(`/classrooms/${classroom.id}/children`);
            return {
              classroom,
              children: toArray<ClassroomChild>(response.data),
            };
          })
        );

        const mismatchChildren = new Map<string, ClassroomChild>();
        childrenByClassroom.forEach(({ children }) => {
          children
            .filter((child) => child.current !== true)
            .forEach((child) => mismatchChildren.set(String(child.id), child));
        });

        const currentLabels = new Map<string, string>();
        await Promise.all(
          Array.from(mismatchChildren.values()).map(async (child) => {
            const response = await http.get(
              `/child-classrooms/classrooms-for-child/${child.id}`
            );
            const currentAssignment = toArray<ClassroomAssignment>(response.data).find(
              (assignment) => assignment.current === true
            );
            currentLabels.set(
              String(child.id),
              currentAssignment
                ? schoolYearLabel(currentAssignment.startDate, currentAssignment.endDate)
                : t("signals.currentSchoolYear.noCurrentYear", {
                    defaultValue: "No current school year",
                  })
            );
          })
        );

        const nextChecks = childrenByClassroom.map(({ classroom, children }) => {
          const mismatches = children
            .filter((child) => child.current !== true)
            .map((child) => ({
              ...child,
              currentSchoolYearLabel:
                currentLabels.get(String(child.id)) ?? "—",
            }))
            .sort((left, right) =>
              childName(left).localeCompare(childName(right), undefined, {
                sensitivity: "base",
              })
            );

          return {
            classroom,
            total: children.length,
            correct: children.length - mismatches.length,
            mismatches,
          };
        });

        if (!cancelled) {
          setYear(currentYear);
          setChecks(nextChecks);
        }
      } catch (caught) {
        if (!cancelled) setError(mapHttpErrorToMessage(caught, t));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const mismatchCount = useMemo(
    () => checks.reduce((total, check) => total + check.mismatches.length, 0),
    [checks]
  );

  const toggleClassroom = (classroomId: Identifier): void => {
    const key = String(classroomId);
    setExpandedClassrooms((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const markAsCurrent = async (
    child: ChildMismatch,
    classroom: Classroom
  ): Promise<void> => {
    const childId = String(child.id);
    setUpdatingChildren((current) => new Set(current).add(childId));
    setActionErrors((current) => {
      const next = { ...current };
      delete next[childId];
      return next;
    });

    try {
      await http.post("/reports/current-school-year/set-current", null, {
        params: { childId: child.id, classroomId: classroom.id },
      });
      setChecks((current) =>
        current.map((check) =>
          String(check.classroom.id) === String(classroom.id)
            ? {
                ...check,
                correct: check.correct + 1,
                mismatches: check.mismatches.filter(
                  (item) => String(item.id) !== childId
                ),
              }
            : check
        )
      );
    } catch (caught) {
      const mapped = mapHttpErrorToMessage(caught, t);
      setActionErrors((current) => ({
        ...current,
        [childId]: mapped.message ?? t("error.error_message", {
          defaultValue: "Something went wrong.",
        }),
      }));
    } finally {
      setUpdatingChildren((current) => {
        const next = new Set(current);
        next.delete(childId);
        return next;
      });
    }
  };

  if (loading) return <Spinner />;
  if (error) return <ErrorPage message={error.message} status={error.status} />;

  const targetYear = year
    ? schoolYearLabel(year.startDate, year.endDate)
    : "—";

  const classroomTeachers = (classroom: Classroom): string[] =>
    (classroom.teachers ?? [])
      .filter((teacher) => !teacher.endDate)
      .map((teacher) => teacherName(teacher))
      .filter((name): name is string => Boolean(name));

  const classroomDisplayName = (classroom: Classroom): string => {
    if (String(classroom.classType).toUpperCase() !== "KINDERGARTEN") {
      return translateClassName(classroom) || classroom.className;
    }

    const label = String(
      t("classrooms.kindergarten", {
        number: "",
        defaultValue: "Kindergarten {{number}}",
      })
    ).trim();
    const teachers = classroomTeachers(classroom);
    return teachers.length > 0 ? `${label} ${teachers.join(", ")}` : label;
  };

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaCalendarCheck}
        eyebrow={t("signals.overview.title", { defaultValue: "Signals" })}
        title={t("signals.currentSchoolYear.title", {
          defaultValue: "Current school year check",
        })}
        description={t("signals.currentSchoolYear.description", {
          defaultValue:
            "Check whether children assigned to this school year also have that assignment marked as current.",
        })}
        actions={
          <span className={`rounded-full border px-4 py-2 text-sm font-semibold ${
            mismatchCount > 0
              ? "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200"
              : "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-200"
          }`}>
            {mismatchCount > 0
              ? t("signals.currentSchoolYear.deviations", {
                  count: mismatchCount,
                  defaultValue: "{{count}} deviations",
                })
              : t("signals.currentSchoolYear.noDeviations", {
                  defaultValue: "No deviations",
                })}
          </span>
        }
      />

      <LdsOverviewSection
        title={t("signals.currentSchoolYear.schoolYear", {
          year: targetYear,
          defaultValue: "School year {{year}}",
        })}
        description={t("signals.currentSchoolYear.readOnly", {
          defaultValue: "This overview is read-only and does not change child assignments.",
        })}
      >
        {!year ? (
          <div className="rounded-3xl border border-dashed border-slate-300 p-8 text-center text-slate-600 dark:border-slate-700 dark:text-slate-300">
            {t("signals.currentSchoolYear.noActiveYear", {
              defaultValue: "No active school year was found.",
            })}
          </div>
        ) : (
          <div className="space-y-4">
            {checks.map((check) => {
              const hasMismatch = check.mismatches.length > 0;
              const expanded = expandedClassrooms.has(String(check.classroom.id));
              const teachers = classroomTeachers(check.classroom);
              const displayName = classroomDisplayName(check.classroom);
              const isKindergarten =
                String(check.classroom.classType).toUpperCase() === "KINDERGARTEN";
              return (
                <article
                  key={String(check.classroom.id)}
                  className={`overflow-hidden rounded-3xl border bg-white shadow-sm dark:bg-slate-900 ${
                    hasMismatch
                      ? "border-amber-200 dark:border-amber-400/30"
                      : "border-emerald-200 dark:border-emerald-400/30"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => hasMismatch && toggleClassroom(check.classroom.id)}
                    disabled={!hasMismatch}
                    aria-expanded={hasMismatch ? expanded : undefined}
                    className="flex w-full items-center gap-4 px-5 py-4 text-left disabled:cursor-default sm:px-6"
                  >
                    <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${
                      hasMismatch
                        ? "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"
                        : "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                    }`}>
                      {hasMismatch ? <FaExclamationTriangle /> : <FaCheckCircle />}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block text-lg font-bold text-slate-950 dark:text-white">
                        {displayName}
                      </span>
                      {!isKindergarten || teachers.length === 0 ? (
                        <span className="mt-1 block text-sm text-slate-600 dark:text-slate-300">
                          {teachers.length > 0
                            ? t("signals.currentSchoolYear.teachers", {
                                teachers: teachers.join(", "),
                                defaultValue: "Teachers: {{teachers}}",
                              })
                            : t("classrooms.table.no_teacher_assigned", {
                                defaultValue: "No teacher assigned",
                              })}
                        </span>
                      ) : null}
                      <span className="mt-1 block text-sm text-slate-500 dark:text-slate-400">
                        {hasMismatch
                          ? t("signals.currentSchoolYear.classHasDeviations", {
                              count: check.mismatches.length,
                              defaultValue: "{{count}} deviations",
                            })
                          : t("signals.currentSchoolYear.classCorrect", {
                              defaultValue: "All children are correct",
                            })}
                      </span>
                    </span>

                    <span className="hidden grid-cols-3 gap-6 text-center sm:grid">
                      <span><strong className="block text-lg text-slate-950 dark:text-white">{check.total}</strong><small className="text-slate-500 dark:text-slate-400">{t("common.total", { defaultValue: "Total" })}</small></span>
                      <span><strong className="block text-lg text-emerald-700 dark:text-emerald-300">{check.correct}</strong><small className="text-slate-500 dark:text-slate-400">{t("signals.currentSchoolYear.correct", { defaultValue: "Correct" })}</small></span>
                      <span><strong className="block text-lg text-amber-700 dark:text-amber-300">{check.mismatches.length}</strong><small className="text-slate-500 dark:text-slate-400">{t("signals.currentSchoolYear.incorrect", { defaultValue: "Incorrect" })}</small></span>
                    </span>

                    {hasMismatch ? (
                      expanded ? <FaChevronUp aria-hidden="true" /> : <FaChevronDown aria-hidden="true" />
                    ) : null}
                  </button>

                  {hasMismatch && expanded ? (
                    <div className="border-t border-slate-200 px-5 py-4 dark:border-white/10 sm:px-6">
                      <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-white/10">
                        {check.mismatches.map((child) => (
                          <div
                            key={String(child.id)}
                            className="grid gap-3 border-b border-slate-200 px-4 py-3 last:border-b-0 dark:border-white/10 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-center"
                          >
                            <button
                              type="button"
                              onClick={() => navigate(`/family/children/${child.id}/details`)}
                              className="text-left font-semibold text-slate-950 hover:text-indigo-700 dark:text-white dark:hover:text-indigo-300"
                            >
                              {childName(child)}
                            </button>
                            <span className="text-sm text-slate-600 dark:text-slate-300">{displayName} · {targetYear}</span>
                            <span className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                              {t("signals.currentSchoolYear.currentValue", {
                                year: child.currentSchoolYearLabel,
                                defaultValue: "Current: {{year}} — does not match",
                              })}
                            </span>
                            {canManageCurrentSchoolYearSignal ? (
                              <div className="sm:text-right">
                                <button
                                  type="button"
                                  disabled={updatingChildren.has(String(child.id))}
                                  onClick={() => void markAsCurrent(child, check.classroom)}
                                  className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-wait disabled:opacity-60"
                                >
                                  <FaCheck aria-hidden="true" />
                                  {updatingChildren.has(String(child.id))
                                    ? t("signals.currentSchoolYear.updating", {
                                        defaultValue: "Updating...",
                                      })
                                    : t("signals.currentSchoolYear.markCurrent", {
                                        defaultValue: "Mark as current",
                                      })}
                                </button>
                                {actionErrors[String(child.id)] ? (
                                  <p className="mt-2 max-w-48 text-xs font-medium text-rose-600 dark:text-rose-300">
                                    {actionErrors[String(child.id)]}
                                  </p>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        )}
      </LdsOverviewSection>
    </LdsOverviewPage>
  );
}
